import { useLayoutEffect, useRef } from 'react'
import Button from '@atoms/Button'
import Link from '@atoms/Link'
import Icon from '@components/utils/Icon'
import { SpriteUrlProvider } from '@components/utils/spriteUrl'
import { useLocalStorage } from '@hooks/useLocalStorage'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('components.announcement_banner')
const tShared = scopedTranslation('shared.buttons')

export type Announcement = { id: string; href: string }

// The announcement every signed-in page shows, or null for none. Its wording is
// body and read_more in config/locales/v2/views/components/announcement_banner.en.yml.
const CURRENT_ANNOUNCEMENT: Announcement | null = null

export type AnnouncementBannerProps = { spriteUrl?: string; announcement?: Announcement | null }

export function AnnouncementBanner({ spriteUrl, announcement = CURRENT_ANNOUNCEMENT }: AnnouncementBannerProps) {
  const [dismissed, setDismissed] = useLocalStorage(`dismissed:announcement:${announcement?.id}`, false)
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const setHeight = () => {
      document.documentElement.style.setProperty('--sb-banner-height', `${node.offsetHeight}px`)
      document.body.style.paddingTop = `${node.offsetHeight}px`
    }
    const observer = new ResizeObserver(setHeight)
    observer.observe(node)
    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty('--sb-banner-height')
      document.body.style.removeProperty('padding-top')
    }
  }, [dismissed])

  if (!announcement || dismissed) return null

  return (
    <SpriteUrlProvider url={spriteUrl}>
      <div
        ref={ref}
        data-testid="announcement-banner"
        className="tw:fixed tw:inset-x-0 tw:top-0 tw:z-40 tw:flex tw:items-center tw:gap-3 tw:border-b tw:border-primary-200 tw:bg-primary-50 tw:px-4 tw:py-2 tw:text-base tw:text-primary-700"
      >
        <Icon type="info-circle" width={20} height={20} className="tw:shrink-0" />
        <p className="tw:flex-1">
          {t('body')}{' '}
          <Link href={announcement.href} rank="link" size="sm" target="_blank" rel="noopener noreferrer">
            {t('read_more')}
          </Link>
        </p>
        <Button
          rank="secondary"
          size="sm"
          leadingIcon="x-close"
          ariaLabel={tShared('close')}
          onClick={() => setDismissed(true)}
          className="tw:shrink-0 tw:px-2"
        />
      </div>
    </SpriteUrlProvider>
  )
}
